<template>
  <Header />
  <div class="section container">
    <div class="content" v-if="requestCompleted && !orderConfirmed">
      <div class="heading">
        <h1>Confirm Shipping Address</h1>
      </div>
      <div class="order-info">
        <div>Order Number: #{{ order.order_number }}</div>
        <div>Recipient: {{ order.customer.first_name }} {{ order.customer.last_name }}</div>
      </div>
      <div class="form">
        <div class="form-group">
          <label class="form-label" for="savedAddress">Saved Addresses</label>
          <div class="select">
            <select class="form-control" id="savedAddress" v-model="defaultAddress" @change="updateAddress">
              <option :key="index" :value="item" v-for="(item, index)  in savedAddresses">{{ formatAddress(item) }}
              </option>
            </select>
            <i class="icon icon--down"></i>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label" for="first_name">First Name *</label>
          <input type="text" id="first_name" class="form-control" v-model="shipping.first_name" @focusout="validateFirstName()">
          <span v-if="errors.first_name" class="form-error">First name is required</span>
        </div>
        <div class="form-group">
          <label class="form-label" for="last_name">Last Name *</label>
          <input type="text" id="last_name" class="form-control" v-model="shipping.last_name" @focusout="validateLastName()">
          <span v-if="errors.last_name" class="form-error">Last name is required</span>
        </div>
        <div class="form-group">
          <label class="form-label" for="address1">Address 1 *</label>
          <input type="text" id="address1" class="form-control" v-model="shipping.address1" @input="validateAddress1()" @focusout="validateAddress1()">
          <span v-if="errors.address1" class="form-error">Address must be 30 characters or less</span>
        </div>
        <div class="form-group">
          <label class="form-label" for="address2">Address 2</label>
          <input type="text" id="address2" class="form-control" v-model="shipping.address2" @input="validateAddress2()" @focusout="validateAddress2()">
          <span v-if="errors.address2" class="form-error">Address must be 30 characters or less</span>
        </div>
        <div class="form-group">
          <label class="form-label" for="city">City *</label>
          <input type="text" id="city" class="form-control" v-model="shipping.city">
        </div>
        <div class="form-group">
          <label class="form-label" for="state">State *</label>
          <select v-if="countryProvinces.length" class="form-control" v-model="shipping.province">
            <option value=""/>
            <option :key="index" :value="provinceName" v-for="(provinceName, index) in countryProvinces">{{
                provinceName
              }}
            </option>
          </select>
          <input v-else type="text" id="state" class="form-control" v-model="shipping.province">
        </div>
          <div class="form-group">
          <label class="form-label" for="zip">Zip Code *</label>
          <input type="text" id="zip" class="form-control" v-model="shipping.zip">
        </div>
        <div class="form-group">
          <label class="form-label" for="country">Country</label>
          <select class="form-control" id="country" v-model="shipping.country" @change="switchProvince($event.target.value); onCountryChange()">
            <option value="" />
            <option :key="c.id" :value="c.name" v-for="c in countries">{{ c.name }}</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-label">Phone *</label>
          <input v-model="shipping.phone" type="tel" class="form-control">
        </div>
        <div class="backend-error-information" v-if="errorState">
          <p>Please adjust following:</p>
          <ul>
            <li v-for="error in errorInfo" :key="error.key">
              {{ error }}
            </li>
          </ul>
        </div>
        <div class="buttons">
          <button class="btn btn--primary" type="button" @click="placeOrder" :disabled="isAddressTooLong() || !shipping.first_name || !shipping.last_name || shipping.phone === null || shipping.phone === '' || shipping.address1 === '' || shipping.city === '' || shipping.province === '' || shipping.zip === ''">Place Order</button>
        </div>
      </div>
    </div>
    <div class="content" v-if="orderConfirmed">
      <div class="full-page-confirmation">
        <p>Order confirmed successfully!</p>
      </div>
    </div>
  </div>

</template>

<script>
import Header from '../components/Header.vue';
import api from '@/api';

export default {
  name: 'ConfirmOrder',
  components: {
    Header
  },
  data() {
    return {
      order: {
        id: this.$route.params.orderid,
        customer: {
          first_name: '',
          last_name: ''
        }
      },
      shipping: {
        first_name: '',
        last_name: '',
        address1: '',
        address2: '',
        city: '',
        province: '',
        zip: '',
        country: '',
        phone: ''
      },
      requestCompleted: false,
      orderConfirmed: false,
      errorState: false,
      errorInfo: [],
      errors: {
        first_name: false,
        last_name: false,
        address1: false,
        address2: false
      },
      countries: [],
      countryProvinces: [],
      defaultAddress: {},
      savedAddresses: [],
    }
  },
  methods: {
    placeOrder() {
      const record = { ...this.shipping, ...{ created_by: this.currentUser.name } };
      this.axios.post(`/v1/orders/${this.order.id}/confirmation.json`, {
        record
      }).then(() => {
        this.orderConfirmed = true;
        setTimeout(this.redirectToOrders, 3000);
      }).catch((error) => {
        if (error.response.status == 422) {
          this.errorState = true;
          this.errorInfo = error.response.data.errors.shipping_address;
        } else if (error.response.status == 409) {
          console.error(error) // order no longer in pending state
        }
      });
    },
    redirectToOrders() {
      this.$router.push('/orders/');
    },
    switchProvince(countryName, province = '') {
      this.shipping.province = province;
      const country = this.countries.find(c => c.name === countryName);
      if (country && country.provinces) {
        this.countryProvinces = country.provinces;
      } else {
        this.countryProvinces.length = 0;
      }
    },
    isAddressTooLong() {
      if (this.shipping.country !== 'United States') {
        return false;
      }
      return this.shipping.address1?.length > 30 || this.shipping.address2?.length > 30;
    },
    validateAddress1() {
      if (this.shipping.country === 'United States' && this.shipping.address1 && this.shipping.address1.length > 30) {
        this.errors.address1 = true;
      } else {
        this.errors.address1 = false;
      }
    },
    validateAddress2() {
      if (this.shipping.country === 'United States' && this.shipping.address2 && this.shipping.address2.length > 30) {
        this.errors.address2 = true;
      } else {
        this.errors.address2 = false;
      }
    },
    validateFirstName() {
      this.errors.first_name = !this.shipping.first_name;
    },
    validateLastName() {
      this.errors.last_name = !this.shipping.last_name;
    },
    onCountryChange() {
      this.validateAddress1();
      this.validateAddress2();
    },
    formatAddress(item){
      return `${item.address1}, ${item.address2 ? item.address2+', ': ''}${item.city}, ${item.zip}, ${item.country}`;
    },
    updateAddress() {
      const { address1, address2, city, state: province, zip, phone, country } = this.defaultAddress || {};
      this.shipping.address1 = address1 || '';
      this.shipping.address2 = address2 || '';
      this.shipping.city = city || '';
      this.shipping.province = province || '';
      this.shipping.zip = zip || '';
      this.shipping.phone = phone || '';
      this.shipping.country = country || '';
      // Validate after populating - saved address may exceed 30 chars for US
      this.validateAddress1();
      this.validateAddress2();
    }
  },
  mounted() {
    this.axios({
      method: 'get',
      url: `/v1/orders/numbered/${this.order.id}.json`,
    })
    .then(response => {
      this.requestCompleted = true;
      this.order = response.data.record;

      if (this.order.financial_status === "pending") {
        api.getCustomer(this.order.customer.id).then(response => {
          this.savedAddresses = response.customer_addresses;
          const first_name = response.first_name
          const last_name = response.last_name
          this.defaultAddress = this.savedAddresses.find(address => address.default);
          const { address1, address2, city, zip, state: province, phone, country } = this.defaultAddress || {};
          this.shipping = { address1, address2, city, province, zip, phone, country, first_name, last_name };
          }).catch((error) => { console.warn(error.message) });
      } else {
        this.redirectToOrders()
      }
    })
   .catch((error) => { console.log(error) });

    this.axios.get('/v1/countries.json')
      .then(response => {
        this.countries = response.data;
        if (this.shipping.country) {
          this.switchProvince(this.shipping.country, this.shipping.province);
        }
      })
      .catch((error) => {
        console.log(error)
      });
  }
}
</script>

<style scoped>
.select {
  position: relative;
}

.icon--down {
  position: absolute;
  right: 18px;
  top: calc(50% - 6px);
}

.form-error {
  color: red;
}
</style>
